// Compact month calendar showing which days the schedule fires. Each day
// with one or more projected firings gets a small count badge. Month-by-
// month nav buttons let the user step forward/back.

import { useEffect, useMemo, useState } from 'react';
import {
  addMonths,
  endOfMonth,
  format,
  isSameDay,
  isSameMonth,
  startOfMonth,
  subMonths,
} from 'date-fns';

import { Icon, IconButton } from '@atom/mui';

import classes from './schedule.module.css';

type ForecastEntry = {
  templateId: string;
  templateName: string;
  scheduleId?: string;
  assetId: string;
  assetName: string;
  dueDate: number;
  superseded: boolean;
};

type PreviewRow = {
  assetId: string;
  assetName: string;
  dueDate: number;
  firesNow: boolean;
  // INTERVAL schedules only — read-only service tracking (see scheduleEngine).
  intervalLastServiced?: number;
  intervalAmount?: number;
};

type SchedulePreviewProps = {
  templateId: string;
  scheduleId?: string;
  refreshToken?: number;
};

// Fetch a 90-day forecast so back/forward navigation covers a useful range
// without re-fetching for every click. Adjust if the engine starts honoring
// longer ranges.
const FORECAST_DAYS = 90;

const DAYS_OF_WEEK = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const SchedulePreview = (props: SchedulePreviewProps) => {
  const { templateId, scheduleId, refreshToken } = props;
  const [forecast, setForecast] = useState<ForecastEntry[]>([]);
  const [preview, setPreview] = useState<PreviewRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [viewMonth, setViewMonth] = useState<Date>(() =>
    startOfMonth(new Date()),
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const base = scheduleId
      ? `/api/v1/work-templates/${templateId}/schedules/${scheduleId}`
      : `/api/v1/work-templates/${templateId}/schedule`;
    Promise.all([
      fetch(`${base}/preview`).then(r => r.json()),
      fetch(`${base}/forecast?days=${FORECAST_DAYS}`).then(r => r.json()),
    ])
      .then(([p, f]) => {
        if (cancelled) return;
        setPreview(Array.isArray(p?.rows) ? p.rows : []);
        setForecast(Array.isArray(f) ? f : []);
        setLoading(false);
      })
      .catch(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [templateId, scheduleId, refreshToken]);

  // countsByDay: ISO yyyy-mm-dd → number of projected firings.
  const countsByDay = useMemo(() => {
    const map = new Map<string, number>();
    forecast.forEach(entry => {
      const key = format(new Date(entry.dueDate), 'yyyy-MM-dd');
      map.set(key, (map.get(key) ?? 0) + 1);
    });
    return map;
  }, [forecast]);

  // Build the 6-row × 7-col grid for the current viewMonth. Each cell is a
  // Date; days outside the month are dimmed.
  const cells = useMemo(() => {
    const monthStart = startOfMonth(viewMonth);
    const monthEnd = endOfMonth(viewMonth);
    // Sunday-start grid. Find the Sunday on or before monthStart.
    const gridStart = new Date(monthStart);
    gridStart.setDate(gridStart.getDate() - gridStart.getDay());
    const result: Date[] = [];
    for (let i = 0; i < 42; i++) {
      const d = new Date(gridStart);
      d.setDate(gridStart.getDate() + i);
      result.push(d);
    }
    // Trim trailing all-out-of-month weeks to keep the calendar compact.
    while (result.length > 28 && !isSameMonth(result[result.length - 7], monthEnd)) {
      result.splice(-7, 7);
    }
    return result;
  }, [viewMonth]);

  const today = new Date();
  const firesNowCount = preview.filter(r => r.firesNow).length;

  return (
    <div className={classes.previewBody}>
      <div className={classes.previewSummary}>
        {loading
          ? 'Computing…'
          : `${firesNowCount} fire today · ${forecast.length} in next ${FORECAST_DAYS} days`}
      </div>

      <div className={classes.calendar}>
        <div className={classes.calendarHeader}>
          <IconButton
            size="small"
            onClick={() => setViewMonth(m => subMonths(m, 1))}
          >
            <Icon>chevron_left</Icon>
          </IconButton>
          <div className={classes.calendarMonth}>
            {format(viewMonth, 'MMMM yyyy')}
          </div>
          <IconButton
            size="small"
            onClick={() => setViewMonth(m => addMonths(m, 1))}
          >
            <Icon>chevron_right</Icon>
          </IconButton>
        </div>

        <div className={classes.calendarGrid}>
          {DAYS_OF_WEEK.map((d, i) => (
            <div key={`dow-${i}`} className={classes.calendarDow}>
              {d}
            </div>
          ))}
          {cells.map(d => {
            const key = format(d, 'yyyy-MM-dd');
            const count = countsByDay.get(key) ?? 0;
            const isThisMonth = isSameMonth(d, viewMonth);
            const isToday = isSameDay(d, today);
            return (
              <div
                key={key}
                className={[
                  classes.calendarCell,
                  !isThisMonth && classes.calendarCellOut,
                  isToday && classes.calendarCellToday,
                ]
                  .filter(Boolean)
                  .join(' ')}
              >
                <span className={classes.calendarDayNum}>{d.getDate()}</span>
                {count > 0 && (
                  <span className={classes.calendarBadge}>{count}</span>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {preview.length > 0 && (
        <ul className={classes.previewList}>
          {preview.slice(0, 6).map(row => {
            // Read-only INTERVAL service tracking: when the PM last fired (the
            // meter value at that service) drives the next boundary. A derived
            // value, not an editable asset field.
            const hasService =
              row.intervalLastServiced != null && row.intervalAmount != null;
            return (
              <li key={row.assetId} className={classes.previewRow}>
                <span>{row.assetName}</span>
                <span style={{ color: 'var(--gray)' }}>
                  {hasService
                    ? `Last serviced: ${row.intervalLastServiced!.toLocaleString()} hr · Next due: ${(
                        row.intervalLastServiced! + row.intervalAmount!
                      ).toLocaleString()} hr`
                    : `${row.firesNow ? 'Fires today · ' : 'Projected · '}${format(
                        new Date(row.dueDate),
                        'M/d/yyyy',
                      )}`}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default SchedulePreview;
